import { Modal } from '@/components/admin/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import type { AdminUser, RbacMatchMode, Skill, UserAttributeDefinition } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

type EditState = { kind: 'create' } | null;

export function AdminRoles() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<EditState>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const rolesQuery = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: api.adminRolesList,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminRoleDelete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'roles'] }),
    onError: (err: unknown) =>
      setServerError(err instanceof ApiError ? err.message : '그룹 삭제에 실패했습니다.'),
  });

  const roles = rolesQuery.data?.roles ?? [];

  return (
    <PageShell
      header={
        <PageHeader
          title="그룹"
          count={roles.length}
          description="그룹에 Skill 을 묶고, 사용자에게 그룹을 할당해 권한을 일괄 관리합니다. 직접 부여(사용자 ↔ Skill) 와 함께 적용됩니다."
          actions={
            <Button onClick={() => setEdit({ kind: 'create' })}>
              <Plus className="size-4" /> 그룹 추가
            </Button>
          }
        />
      }
    >
      {serverError ? (
        <div className="mb-3 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {serverError}
        </div>
      ) : null}

      {rolesQuery.isLoading ? (
        <div className="text-sm text-foreground-muted">불러오는 중…</div>
      ) : roles.length === 0 ? (
        <div className="rounded-md border border-border-muted bg-surface-muted px-4 py-6 text-sm text-foreground-muted">
          등록된 그룹이 없습니다. 상단 "그룹 추가" 로 시작하세요.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-foreground-muted">
              <tr>
                <th className="px-3 py-2 text-left font-medium">이름</th>
                <th className="px-3 py-2 text-left font-medium">설명</th>
                <th className="px-3 py-2 text-right font-medium">Skill</th>
                <th className="px-3 py-2 text-right font-medium">사용자</th>
                <th className="px-3 py-2 text-left font-medium">상태</th>
                <th className="px-3 py-2 text-right font-medium">작업</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-surface-muted/50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <Link
                        to="/admin/roles/$id"
                        params={{ id: r.id }}
                        className="font-medium text-foreground hover:underline"
                      >
                        {r.name}
                      </Link>
                      {r.systemKind === 'all_users' ? (
                        <span className="rounded bg-info/15 px-1.5 py-0.5 text-[10px] font-medium text-info">
                          전체
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-foreground-muted line-clamp-1 max-w-[420px]">
                    {r.description ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.skillIds.length}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {r.systemKind === 'all_users' ? '—' : r.memberCount}
                  </td>
                  <td className="px-3 py-2">
                    {r.enabled ? (
                      <span className="text-xs text-emerald-400">활성</span>
                    ) : (
                      <span className="text-xs text-foreground-muted">비활성</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.systemKind ? null : (
                      <button
                        type="button"
                        aria-label={`${r.name} 삭제`}
                        onClick={() => {
                          if (
                            window.confirm(
                              `'${r.name}' 그룹을 삭제하시겠습니까? 이 그룹으로 부여된 Skill 권한이 즉시 회수됩니다.`,
                            )
                          ) {
                            deleteMutation.mutate(r.id);
                          }
                        }}
                        className="text-foreground-muted hover:text-red-400"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit?.kind === 'create' ? <RoleCreateModal onClose={() => setEdit(null)} /> : null}
    </PageShell>
  );
}

function RoleCreateModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const skillsQuery = useQuery({
    queryKey: ['admin', 'skills'],
    queryFn: api.adminSkillList,
  });
  const skills: Skill[] = (skillsQuery.data?.skills ?? []).filter((s) => s.enabled);

  const createMutation = useMutation({
    mutationFn: () =>
      api.adminRoleCreate({
        name: name.trim(),
        description: description.trim() || null,
        enabled: true,
        skillIds: selectedSkillIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roles'] });
      onClose();
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : '그룹 생성에 실패했습니다.'),
  });

  const toggleSkill = (id: string) =>
    setSelectedSkillIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('그룹 이름을 입력하세요.');
      return;
    }
    createMutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="그룹 추가"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="role-create-form" disabled={createMutation.isPending}>
            {createMutation.isPending ? '생성 중…' : '생성'}
          </Button>
        </div>
      }
    >
      <form id="role-create-form" onSubmit={submit} className="space-y-4">
        {error ? (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>
        ) : null}
        <Field label="이름" htmlFor="role-name">
          <Input
            id="role-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 영업팀, 분석가, 임원"
          />
        </Field>
        <Field label="설명" htmlFor="role-desc">
          <Input
            id="role-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="이 그룹이 무엇을 위한 것인지 (선택)"
          />
        </Field>
        <Field
          label="Skill"
          htmlFor="role-skills"
          hint="이 그룹이 가진 Skill. 나중에 상세 화면에서 변경 가능합니다."
        >
          <div id="role-skills">
            <SkillMultiSelect
              skills={skills}
              value={selectedSkillIds}
              onToggle={toggleSkill}
              isLoading={skillsQuery.isLoading}
            />
          </div>
        </Field>
      </form>
    </Modal>
  );
}

/**
 * 룰 입력용 3가지 operator. 내부적으로는 모두 matchMode='exact' + negate 차원.
 *   eq      → 단일값 입력 권장 (matchMode='exact', negate=false)
 *   in      → 다중값 chip 입력 (matchMode='exact', negate=false)
 *   not_in  → 다중값 chip 입력 (matchMode='exact', negate=true)
 *
 * 내부적으로 eq / in 은 동일 (matchMode='exact', negate=false) — 라벨/입력 UX 만 분리.
 * display 시점에는 chip 개수로 = (1개) / IN (2개+) / NOT IN (negate) 결정.
 *
 * matchMode='prefix' (legacy 하위 포함) 는 신규 입력 UI 에서 제거. 기존 DB 데이터는
 * 표시는 유지 (라벨 "하위 포함" / "하위 미포함") + chip 제거로 정리 가능.
 */
type RuleOperator = 'eq' | 'in' | 'not_in';

function decodeOperator(op: RuleOperator): { matchMode: RbacMatchMode; negate: boolean } {
  switch (op) {
    case 'eq':
    case 'in':
      return { matchMode: 'exact', negate: false };
    case 'not_in':
      return { matchMode: 'exact', negate: true };
  }
}

/** 그룹 표시용 라벨 — matchMode, negate, chip 개수로 결정. */
function groupLabel(matchMode: RbacMatchMode, negate: boolean, count: number): string {
  if (matchMode === 'prefix') return negate ? '하위 미포함' : '하위 포함';
  if (negate) return 'NOT IN';
  return count <= 1 ? '=' : 'IN';
}
function groupToneClass(matchMode: RbacMatchMode, negate: boolean): string {
  if (matchMode === 'prefix')
    return negate ? 'bg-warning/15 text-warning font-medium' : 'bg-info/15 text-info font-medium';
  if (negate) return 'bg-destructive/15 text-destructive font-medium';
  return 'bg-surface-muted text-foreground font-medium';
}

type Rule = {
  attributeKey: string;
  attributeValue: string;
  matchMode: RbacMatchMode;
  negate: boolean;
};

/**
 * 룰 편집기. 내부 데이터는 row 단위 (Plan E PK 와 1:1) 지만 UI 는 같은
 * (attributeKey, matchMode, negate) 끼리 묶어 한 줄로 표현 — value 만 chip 으로 나열.
 *
 * 평가 의미 (서버 matcher 와 일치):
 *   - 같은 attribute / operator 의 chip 들은 OR
 *   - 다른 operator 끼리는 OR (positive) / AND NOT (negate)
 *   - 다른 attribute 끼리는 AND
 */
export function AttributeRulesEditor({
  defs,
  value,
  onChange,
  isLoading,
}: {
  defs: UserAttributeDefinition[];
  value: Rule[];
  onChange: (next: Rule[]) => void;
  isLoading: boolean;
}) {
  const [draftKey, setDraftKey] = useState('');
  const [draftValue, setDraftValue] = useState('');
  const [draftOp, setDraftOp] = useState<RuleOperator>('eq');

  const enumDefs = defs.filter((d) => d.type === 'enum' || d.type === 'text');
  const selectedDef = enumDefs.find((d) => d.key === draftKey);

  // 초기 key 선택 — 첫 enum/text attribute 자동 선택
  if (!draftKey && enumDefs[0]) setDraftKey(enumDefs[0].key);

  // 같은 (key, mode, negate) 끼리 묶기 — UI 표시용.
  const grouped = useMemo(() => {
    const map = new Map<
      string,
      { key: string; matchMode: RbacMatchMode; negate: boolean; values: string[] }
    >();
    for (const r of value) {
      const gk = `${r.attributeKey}|${r.matchMode}|${r.negate}`;
      const g = map.get(gk);
      if (g) g.values.push(r.attributeValue);
      else
        map.set(gk, {
          key: r.attributeKey,
          matchMode: r.matchMode,
          negate: r.negate,
          values: [r.attributeValue],
        });
    }
    // attribute 별로 정렬, 같은 attribute 안에서는 positive 먼저, exact > prefix 순.
    return Array.from(map.values()).sort((a, b) => {
      if (a.key !== b.key) return a.key.localeCompare(b.key);
      const aw = (a.negate ? 2 : 0) + (a.matchMode === 'prefix' ? 1 : 0);
      const bw = (b.negate ? 2 : 0) + (b.matchMode === 'prefix' ? 1 : 0);
      return aw - bw;
    });
  }, [value]);

  // value 가 입력될 때 "쉼표 / 줄바꿈" 으로 여러 값 일괄 입력 지원.
  const addRules = (rawInput: string) => {
    const key = draftKey.trim();
    if (!key) return;
    const { matchMode, negate } = decodeOperator(draftOp);
    const tokens = rawInput
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (tokens.length === 0) return;
    const next = [...value];
    for (const t of tokens) {
      const dup = next.some(
        (r) =>
          r.attributeKey === key &&
          r.attributeValue === t &&
          r.matchMode === matchMode &&
          r.negate === negate,
      );
      if (!dup) next.push({ attributeKey: key, attributeValue: t, matchMode, negate });
    }
    onChange(next);
    setDraftValue('');
  };

  const removeChip = (key: string, matchMode: RbacMatchMode, negate: boolean, val: string) => {
    onChange(
      value.filter(
        (r) =>
          !(
            r.attributeKey === key &&
            r.matchMode === matchMode &&
            r.negate === negate &&
            r.attributeValue === val
          ),
      ),
    );
  };

  if (isLoading) return <div className="text-sm text-foreground-muted">불러오는 중…</div>;

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      {grouped.length === 0 ? (
        <div className="text-sm text-foreground-muted">등록된 룰이 없습니다.</div>
      ) : (
        <ul className="space-y-1.5">
          {grouped.map((g, gi) => {
            const label = groupLabel(g.matchMode, g.negate, g.values.length);
            const tone = groupToneClass(g.matchMode, g.negate);
            // 같은 attribute 의 연속 그룹 = OR, 다른 attribute = AND.
            const prev = grouped[gi - 1];
            const sameAttrAsPrev = prev && prev.key === g.key;
            return (
              <div key={`${g.key}|${g.matchMode}|${g.negate}`}>
                {sameAttrAsPrev ? (
                  <div className="mx-2 mb-1 text-[10px] uppercase tracking-wider text-foreground-muted">
                    또는 (OR)
                  </div>
                ) : gi > 0 ? (
                  <div className="mx-2 my-1.5 text-[10px] uppercase tracking-wider text-foreground-muted">
                    그리고 (AND)
                  </div>
                ) : null}
                <li className="rounded bg-surface-muted px-2 py-1.5 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="rounded bg-surface px-1.5 py-0.5 text-xs font-medium">
                      {g.key}
                    </span>
                    <span className={`rounded px-1.5 py-0.5 text-xs ${tone}`}>{label}</span>
                    {g.values.map((v) => (
                      <span
                        key={v}
                        className="inline-flex items-center gap-1 rounded bg-surface px-1.5 py-0.5 text-xs"
                      >
                        {v}
                        <button
                          type="button"
                          onClick={() => removeChip(g.key, g.matchMode, g.negate, v)}
                          className="text-foreground-muted hover:text-red-300"
                          aria-label={`${v} 제거`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </li>
              </div>
            );
          })}
        </ul>
      )}
      {enumDefs.length === 0 ? (
        <div className="text-xs text-foreground-muted">
          enum 또는 text 타입의 attribute 정의가 없습니다. 관리자 &rsaquo; 사용자 항목 관리에서 먼저
          등록하세요.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 border-t border-border pt-2 md:grid-cols-[1fr_auto_2fr_auto] md:items-end">
          <div>
            <div className="mb-1 text-xs text-foreground-muted">속성</div>
            <select
              value={draftKey}
              onChange={(e) => {
                setDraftKey(e.target.value);
                setDraftValue('');
              }}
              className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
            >
              {enumDefs.map((d) => (
                <option key={d.id} value={d.key}>
                  {d.label} ({d.key})
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs text-foreground-muted">연산자</div>
            <select
              value={draftOp}
              onChange={(e) => setDraftOp(e.target.value as RuleOperator)}
              className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
            >
              <option value="eq">=</option>
              <option value="in">IN</option>
              <option value="not_in">NOT IN</option>
            </select>
          </div>
          <div>
            <div className="mb-1 text-xs text-foreground-muted">
              {draftOp === 'eq' ? '값' : '값 (쉼표로 여러 개)'}
            </div>
            {selectedDef?.type === 'enum' &&
            selectedDef.options &&
            selectedDef.options.length > 0 ? (
              <select
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                className="h-10 w-full rounded-md border border-border bg-surface px-3 text-sm"
              >
                <option value="">선택…</option>
                {selectedDef.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRules(draftValue);
                  }
                }}
                placeholder={draftOp === 'eq' ? '예: AF1100' : '예: AF1100, AF1400, AE1100'}
              />
            )}
          </div>
          <Button
            type="button"
            onClick={() => addRules(draftValue)}
            disabled={!draftKey || !draftValue.trim()}
          >
            추가
          </Button>
        </div>
      )}
    </div>
  );
}

export function SkillMultiSelect({
  skills,
  value,
  onToggle,
  isLoading,
}: {
  skills: Skill[];
  value: string[];
  onToggle: (id: string) => void;
  isLoading: boolean;
}) {
  if (isLoading) return <div className="text-sm text-foreground-muted">불러오는 중…</div>;
  if (skills.length === 0) {
    return (
      <div className="text-sm text-foreground-muted">
        활성화된 Skill 이 없습니다. 관리자 &rsaquo; Skill 페이지에서 추가하세요.
      </div>
    );
  }
  return (
    <div className="space-y-1 rounded-md border border-border p-2 max-h-64 overflow-y-auto">
      {skills.map((s) => {
        const checked = value.includes(s.id);
        return (
          <label
            key={s.id}
            className="flex items-start gap-2 px-2 py-1.5 text-sm hover:bg-surface-muted rounded cursor-pointer"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(s.id)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <div className="font-medium">{s.name}</div>
              {s.description ? (
                <div className="text-xs text-foreground-muted line-clamp-1">{s.description}</div>
              ) : null}
            </div>
          </label>
        );
      })}
    </div>
  );
}

export function UserMultiSelect({
  users,
  value,
  onToggle,
  isLoading,
  disableInternalSearch,
}: {
  users: AdminUser[];
  value: string[];
  onToggle: (id: string) => void;
  isLoading: boolean;
  /** 호출부가 자체 검색 UI 를 제공하는 경우 true. 그 때 users 는 이미 필터된 결과로 가정. */
  disableInternalSearch?: boolean;
}) {
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const filtered = useMemo(() => {
    if (disableInternalSearch || !search) return users;
    return users.filter(
      (u) => u.email.toLowerCase().includes(search) || u.displayName.toLowerCase().includes(search),
    );
  }, [users, search, disableInternalSearch]);

  if (isLoading) return <div className="text-sm text-foreground-muted">불러오는 중…</div>;
  if (users.length === 0) {
    return (
      <div className="text-sm text-foreground-muted">
        일반 사용자가 없습니다. admin 사용자는 그룹 부여 대상이 아닙니다.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {disableInternalSearch ? null : (
        <Input
          placeholder="이메일 / 이름 검색"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
        />
      )}
      <div className="space-y-1 rounded-md border border-border p-2 max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-foreground-muted">검색 결과 없음</div>
        ) : (
          filtered.map((u) => {
            const checked = value.includes(u.id);
            return (
              <label
                key={u.id}
                className="flex items-start gap-2 px-2 py-1.5 text-sm hover:bg-surface-muted rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(u.id)}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <div className="font-medium">{u.displayName}</div>
                  <div className="text-xs text-foreground-muted">{u.email}</div>
                </div>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
